import React from 'react';
import { CheckCircle2, AlertTriangle, Target, Lightbulb } from 'lucide-react';

const getScoreColor = (score, maxScore) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return 'score-high';
    if (percentage >= 50) return 'score-medium';
    return 'score-low';
};

function getLetterGrade(percentage) {
    if (percentage >= 90) return 'A*';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B';
    if (percentage >= 60) return 'C';
    if (percentage >= 50) return 'D';
    if (percentage >= 40) return 'E';
    return 'U';
}

export function FeedbackResult({ feedback }) {
    if (!feedback) return null;

    const percentage = Math.round((feedback.score / feedback.max_score) * 100);
    const grade = getLetterGrade(percentage);

    return (
        <div className="brutalist-card p-6 fade-in" data-testid="feedback-result">
            {/* Grade & Score */}
            <div className="text-center mb-6">
                <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full border-4 border-zinc-950 ${getScoreColor(feedback.score, feedback.max_score)}`}>
                    <span className="text-3xl font-black" data-testid="feedback-grade">{grade}</span>
                </div>
                <p className="mt-3 text-base font-bold text-zinc-950" data-testid="feedback-current-grade">
                    Current Grade: {grade}
                </p>
                <p className="mt-1 text-sm font-semibold text-zinc-700" data-testid="feedback-marks">
                    Marks: {feedback.score}/{feedback.max_score}
                </p>
                <p className="mt-0.5 text-sm font-semibold text-zinc-700" data-testid="feedback-percentage">
                    Percentage: {percentage}%
                </p>
            </div>

            {/* Feedback Summary */}
            <div className="mb-6">
                <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    Summary
                </h3>
                <p className="text-zinc-700 leading-relaxed" data-testid="feedback-summary">
                    {feedback.feedback_summary}
                </p>
            </div>

            {/* Missing Points */}
            <MissingPointsList points={feedback.missing_points} />

            {/* Improvement Tips */}
            <ImprovementTipsList tips={feedback.improvement_tips} />

            {/* Full Mark Answer */}
            <div>
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                    <Target className="w-5 h-5 text-green-600" />
                    Full Mark Answer
                </h3>
                <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                    <p className="text-zinc-700 leading-relaxed whitespace-pre-wrap" data-testid="full-mark-answer">
                        {feedback.full_mark_answer}
                    </p>
                </div>
            </div>
        </div>
    );
}

export function MissingPointsList({ points }) {
    if (!points || points.length === 0) return null;

    return (
        <div className="mb-6">
            <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Missing Points
            </h3>
            <ul className="space-y-2" data-testid="missing-points-list">
                {points.map((point, index) => (
                    <li key={`missing-${index}-${point.slice(0, 20)}`} className="flex items-start gap-2 text-zinc-700">
                        <span className="inline-block w-5 h-5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold flex-shrink-0 flex items-center justify-center mt-0.5">
                            {index + 1}
                        </span>
                        <span>{point}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function ImprovementTipsList({ tips }) {
    if (!tips || tips.length === 0) return null;

    return (
        <div className="mb-6">
            <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-blue-600" />
                How to Achieve Full Marks
            </h3>
            <ul className="space-y-3" data-testid="improvement-tips-list">
                {tips.map((tip, index) => (
                    <li key={`tip-${index}-${tip.slice(0, 20)}`} className="p-3 bg-blue-50 border-2 border-blue-200 rounded-lg text-zinc-700">
                        <span className="font-semibold text-blue-800">Tip {index + 1}:</span> {tip}
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function FeedbackPlaceholder() {
    return (
        <div className="brutalist-card p-8" data-testid="feedback-placeholder">
            <div className="text-center">
                <div className="w-20 h-20 rounded-full skeleton mx-auto mb-5" />
                <div className="skeleton skeleton-text w-32 mx-auto" />
                <div className="skeleton skeleton-text-sm w-24 mx-auto" />
                <div className="skeleton skeleton-text-sm w-24 mx-auto mb-6" />
                <h3 className="text-xl font-bold mb-2">Your Feedback</h3>
                <p className="text-zinc-500 text-sm">
                    Submit a question and answer to receive your AI-graded feedback here.
                </p>
            </div>
        </div>
    );
}
